import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ArrowRight, Check, Loader2, RefreshCw, ShieldCheck, TriangleAlert, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { Banner, LeadsTile, QualityBar, SheetTile, StatTile, TONES, qualityPct } from './SheetImportDetailsModal';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

const STEPS = ['Connecting to Google Sheets', 'Reading rows', 'Mapping columns', 'Validating data'];

export type ResyncPhase = 'confirm' | 'running' | 'done' | 'error';

export interface ResyncResult {
  import_id: string;
  total_rows: number;
  imported_count: number;
  failed_count: number;
}

interface ResyncModalProps {
  visible: boolean;
  phase: ResyncPhase;
  /** Short label of the sheet, e.g. the sheet id */
  target?: string;
  result: ResyncResult | null;
  error: string | null;
  onCancel: () => void; // closes (confirm / done / error)
  onConfirm: () => void; // starts (confirm) or retries (error)
  onReview: (importId: string) => void;
}

const ResyncModal: React.FC<ResyncModalProps> = ({ visible, phase, target, result, error, onCancel, onConfirm, onReview }) => {
  const [step, setStep] = useState(0);
  const running = phase === 'running';

  useEffect(() => {
    if (!running) return;
    setStep(0);
    const t = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 700);
    return () => clearInterval(t);
  }, [running]);

  const handleHide = () => {
    if (!running) onCancel();
  };

  const title =
    phase === 'confirm' ? 'Re-sync this sheet?' : phase === 'running' ? 'Re-syncing…' : phase === 'done' ? 'Re-sync complete' : 'Re-sync failed';

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: phase === 'error' ? 'linear-gradient(135deg,#c62828,#e53935)' : `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
          color: phase === 'error' ? '#fff' : C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        {phase === 'error' ? <TriangleAlert size={20} /> : phase === 'done' ? <Check size={20} strokeWidth={3} /> : <RefreshCw size={20} className={running ? 'rsm-spin' : undefined} />}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>{title}</div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          {target ? (
            <>
              Sheet <strong style={{ color: C.teal, fontFamily: 'monospace' }}>{target}</strong>
            </>
          ) : (
            'Google Sheets → Pitchout Leads'
          )}
        </div>
      </div>
    </div>
  );

  const flowLabel = phase === 'running' ? 'Syncing' : phase === 'done' ? 'Synced' : phase === 'error' ? 'Stopped' : 'Ready';
  const flowActive = phase === 'running' || phase === 'done';

  const flowNode = (tile: React.ReactNode, label: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6.5px' }}>
      {tile}
      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );

  const flow = (
    <div
      className={running ? 'rsm-run' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '16px 18px',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #0c403c 0%, #115550 50%, #16706a 100%)',
        border: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      {flowNode(<SheetTile size={54} />, 'Google Sheets')}
      <div style={{ position: 'relative', width: '96px', height: '22px', marginBottom: '18px' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: '2px dashed rgba(255,255,255,0.25)' }} />
        <span className="rsm-dot a" />
        <span className="rsm-dot b" style={{ animationDelay: '0.4s' }} />
        <div
          style={{
            position: 'absolute',
            top: '-12px',
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: '0.54rem',
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: flowActive ? C.lime : 'rgba(255,255,255,0.5)',
          }}
        >
          {flowLabel}
        </div>
      </div>
      {flowNode(<LeadsTile size={54} />, 'Pitchout Leads')}
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="rsm-dialog"
      maskClassName="rsm-mask"
      style={{ width: '90vw', maxWidth: '500px' }}
      modal
      closable={!running}
      dismissableMask={!running}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .rsm-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: rsmMaskIn 0.3s ease; }
        .rsm-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: rsmDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
        .rsm-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .rsm-dialog .p-dialog-header-icon { color: ${C.sub}; }
        .rsm-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; max-height: 72vh; overflow-y: auto; }
        @keyframes rsmMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes rsmDlgIn { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .rsm-spin { animation: rsmSpin 0.9s linear infinite; }
        @keyframes rsmSpin { to { transform: rotate(360deg); } }
        .rsm-dot { position:absolute; top:50%; margin-top:-4px; width:8px; height:8px; border-radius:50%; background:${C.lime}; box-shadow:0 0 10px rgba(200,233,75,0.9); }
        .rsm-dot.a { left:0; }
        .rsm-dot.b { left:calc(100% - 8px); }
        .rsm-run .rsm-dot { animation: rsmFlow 0.8s ease-in-out infinite; }
        @keyframes rsmFlow { 0% { left:0; opacity:0; } 15% { opacity:1; } 85% { opacity:1; } 100% { left:calc(100% - 8px); opacity:0; } }
        .rsm-go:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(17,85,80,0.35) !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {flow}

        {/* ── Confirm ───────────────────────────────────────────── */}
        {phase === 'confirm' && (
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#374151', lineHeight: 1.6 }}>
                This re-reads the Google Sheet and replaces the staged rows of this sync with the latest data.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  background: 'rgba(22,163,74,0.06)',
                  border: '1px solid rgba(22,163,74,0.2)',
                  fontSize: '0.7rem',
                  color: '#14532d',
                  lineHeight: 1.5,
                }}
              >
                <ShieldCheck size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: '1px' }} />
                Nothing is added to Leads. This sync hasn’t been pushed yet, so you can still review before pushing.
              </div>
            </div>
          </div>
        )}

        {/* ── Running ───────────────────────────────────────────── */}
        {phase === 'running' && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {STEPS.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <div
                  key={s}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 10.1px',
                    borderRadius: '20px',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    transition: 'all 0.3s',
                    background: active ? 'rgba(17,85,80,0.08)' : G.shellBg,
                    border: `1px solid ${active ? 'rgba(17,85,80,0.3)' : G.line}`,
                    color: done || active ? C.teal : C.muted,
                  }}
                >
                  {done ? (
                    <Check size={11} strokeWidth={3} color="#16a34a" />
                  ) : active ? (
                    <Loader2 size={11} className="rsm-spin" />
                  ) : (
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d1d5db' }} />
                  )}
                  {s}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Result ────────────────────────────────────────────── */}
        {phase === 'done' && result && (
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '0.68rem', color: C.sub }}>
                Data quality <strong style={{ color: C.teal }}>{qualityPct(result.imported_count, result.failed_count)}%</strong> · staged for review
              </div>
              <div style={{ display: 'flex', gap: '6.5px', flexWrap: 'wrap' }}>
                <StatTile label="Rows Read" value={result.total_rows} />
                <StatTile label="Ready" value={result.imported_count} color="#15803d" />
                <StatTile label="Invalid" value={result.failed_count} color="#c62828" />
              </div>
              {result.total_rows > 0 && <QualityBar valid={result.imported_count} invalid={result.failed_count} showLabels={false} />}
              <Banner
                tone={result.imported_count > 0 ? TONES.lime : TONES.amber}
                icon={result.imported_count > 0 ? <ShieldCheck size={15} /> : <TriangleAlert size={15} />}
              >
                {result.imported_count > 0
                  ? `${result.imported_count} row${result.imported_count > 1 ? 's are' : ' is'} ready. Nothing is in your leads yet — review and push when you're happy.`
                  : 'No rows passed validation. Fix the sheet and re-sync again.'}
              </Banner>
            </div>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────── */}
        {phase === 'error' && (
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '4.6px' }}>
              <Banner tone={TONES.red} icon={<TriangleAlert size={15} />}>
                {error || 'Re-sync failed. Your previous rows were kept.'}
              </Banner>
            </div>
          </div>
        )}

        {/* ── Actions ───────────────────────────────────────────── */}
        {phase !== 'running' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', marginTop: '2px' }}>
            <GlobalButton label={phase === 'done' ? 'Close' : 'Cancel'} variant="secondary" icon={X} onClick={onCancel} />
            {(phase === 'confirm' || phase === 'error') && (
              <GlobalButton
                label={phase === 'error' ? 'TRY AGAIN' : 'RE-SYNC'}
                variant="primary"
                icon={RefreshCw}
                onClick={onConfirm}
              />
            )}
            {phase === 'done' && result && result.imported_count > 0 && (
              <button
                onClick={() => onReview(result.import_id)}
                className="rsm-go"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0 18px',
                  height: '36px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'rgb(17,85,80)',
                  color: '#fff',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  letterSpacing: '0.3px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(17,85,80,0.25)',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                REVIEW & PUSH <ArrowRight size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ResyncModal;
